import { getParam, hasFlag } from '@amusnet/react-ui'
import { useConfigurationStore } from '@stores'

// Google Analytics 4 (gtag.js) for the whole game UI. On by default; a launch URL can point the game
// at another GA4 stream with `gaMeasurementId=G-XXXXXXXXXX`, or switch tracking off entirely with the
// `shouldDisableAnalytics` flag.
const DEFAULT_MEASUREMENT_ID = 'G-0KXL9XMQT7'

let isInitialized = false

/**
 * gtag.js only executes a dataLayer entry as a command when it is a real `arguments` object — it
 * checks `Object.prototype.toString.call(x) === '[object Arguments]'`. A plain array is treated as a
 * legacy method-call push and silently discarded, so this has to stay a `function` (an arrow has no
 * `arguments`) and push `arguments` rather than a rest array.
 */
// The signature only types the call sites; the values are read off `arguments` instead.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function gtag(..._args: unknown[]) {
    window.dataLayer = window.dataLayer ?? []
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer.push(arguments)
}

const loadScript = (measurementId: string) => {
    const script = document.createElement('script')

    script.async = true
    script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`
    document.head.appendChild(script)
}

/**
 * Loads gtag.js and configures the GA4 stream. Idempotent — safe to call from more than one place.
 * Call it once the SHOW payload has been applied to the configuration store, so playerUid/portalCode
 * are available as identity/dimensions.
 *
 * Events queued into window.dataLayer before the script finishes loading are replayed by gtag.js on
 * load, so callers never have to wait for it.
 */
export const initAnalytics = () => {
    if (isInitialized || hasFlag('shouldDisableAnalytics')) return

    const measurementId = getParam('gaMeasurementId')?.trim() || DEFAULT_MEASUREMENT_ID

    if (!measurementId.startsWith('G-')) return

    isInitialized = true

    loadScript(measurementId)

    gtag('js', new Date())
    gtag('config', measurementId, {
        // The game runs inside the operator's iframe, where third-party cookies are unreliable or
        // outright blocked: let GA manage no storage at all and identify the session ourselves from
        // playerUid, so sessions don't fragment (and no consent-gated storage is written).
        client_storage: 'none',
        client_id: useConfigurationStore.getState().playerUid || undefined,
        // A game canvas has no navigable pages; screens are reported as explicit events instead.
        send_page_view: false,
    })
}

/**
 * Dimensions attached to every event. Read at send time rather than set once as user_properties:
 * gin/currency arrive with SETUP, which lands after analytics is already initialised.
 */
const commonParams = () => {
    const { portalCode, gin, currency, playerUid } = useConfigurationStore.getState()

    return {
        portal_code: portalCode || undefined,
        // -1 is the store's "not set yet" default.
        gin: gin > 0 ? gin : undefined,
        currency: currency || undefined,
        player_uid: playerUid || undefined,
    }
}

/** Sends a GA4 event. No-op until initAnalytics() has enabled tracking. */
export const trackEvent = (name: string, params: Record<string, unknown> = {}) => {
    if (!isInitialized) return

    // History/replay re-runs the same UI code over a past round; those events are not real play. The
    // flag arrives with SETUP — after init — so it has to be checked per event, not once at startup.
    const { isHistoryMode, isReplayMode } = useConfigurationStore.getState()

    if (isHistoryMode || isReplayMode) return

    gtag('event', name, { ...commonParams(), ...params })
}

/** Test-only: drops the module's memo so initAnalytics() can run again against a fresh DOM. */
export const resetAnalyticsForTests = () => {
    isInitialized = false
    delete window.dataLayer
}
